export type EntityStatusTab = "All" | "Active" | "Inactive" | "Pending" | "Suspended";

export const ENTITY_STATUS_TABS: EntityStatusTab[] = [
  "All",
  "Active",
  "Inactive",
  // "Pending",
  // "Suspended",
];

export const ENTITY_STATUS_SELECT_OPTIONS = ENTITY_STATUS_TABS.filter(
  (status) => status !== "All",
).map((status) => ({ value: status, label: status }));

export const ENTITY_STATUS_API_OPTIONS = ENTITY_STATUS_SELECT_OPTIONS.map((option) => ({
  value: option.value.toUpperCase(),
  label: option.label,
}));

export const ENTITY_STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  active: { bg: "rgba(46,213,115,0.12)", color: "#2ed573" },
  inactive: { bg: "rgba(148,163,184,0.12)", color: "#94a3b8" },
  pending: { bg: "rgba(255,165,2,0.12)", color: "#ffa502" },
  suspended: { bg: "rgba(255,71,87,0.12)", color: "#ff4757" },
  // Reuse the active/suspended color tokens — same semantic meaning (approved/rejected), just
  // different wording for the storefront URL approval workflow.
  approved: { bg: "rgba(46,213,115,0.12)", color: "#2ed573" },
  rejected: { bg: "rgba(255,71,87,0.12)", color: "#ff4757" },
  success: { bg: "rgba(46,213,115,0.12)", color: "#2ed573" },
  succeeded: { bg: "rgba(46,213,115,0.12)", color: "#2ed573" },
  failed: { bg: "rgba(255,71,87,0.12)", color: "#ff4757" },
  failure: { bg: "rgba(255,71,87,0.12)", color: "#ff4757" },
  error: { bg: "rgba(255,71,87,0.12)", color: "#ff4757" },
  unknown: { bg: "rgba(148,163,184,0.12)", color: "#94a3b8" },
};

export const DEFAULT_STATUS_STYLE = ENTITY_STATUS_STYLE.unknown;

export type OrderStatusKey =
  | "completed"
  | "failed"
  | "pending"
  | "processing"
  | "awaiting_checkout"
  | "refunded";

export const AWAITING_CHECKOUT_STATUS_STYLE = {
  bg: "rgba(139, 92, 246, 0.14)",
  color: "#7C3AED",
};

export const REFUNDED_STATUS_STYLE = {
  bg: "rgba(56, 189, 248, 0.14)",
  color: "#0EA5E9",
};

export const ORDER_STATUS_FILTER_TABS: { value: string; label: string }[] = [
  { value: "all", label: "All" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "pending", label: "Pending" },
  { value: "awaiting_checkout", label: "Awaiting Checkout" },
  { value: "refunded", label: "Refunded" },
  { value: "chargeback", label: "Chargeback" },
];

export function normalizeStatusKey(status: unknown): string {
  return String(status ?? "").trim().toLowerCase();
}

export function formatEntityStatusLabel(status: unknown): string {
  const key = normalizeStatusKey(status);
  if (!key) return "Unknown";
  return key
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function matchesEntityStatus(status: unknown, filterTab: string): boolean {
  if (filterTab === "All") return true;
  return normalizeStatusKey(status) === filterTab.toLowerCase();
}

export function getEntityStatusStyle(status: unknown) {
  const key = normalizeStatusKey(status);
  return ENTITY_STATUS_STYLE[key] ?? DEFAULT_STATUS_STYLE;
}

export function toApiEntityStatus(status: unknown): string {
  const key = normalizeStatusKey(status);
  const match = ENTITY_STATUS_API_OPTIONS.find(
    (option) => option.value.toLowerCase() === key,
  );
  return match?.value ?? (String(status ?? "ACTIVE").trim().toUpperCase() || "ACTIVE");
}

export function mapToOrderStatusKey(status: unknown): string {
  return normalizeStatusKey(status);
}

export function getOrderStatusStyle(status: unknown) {
  const key = mapToOrderStatusKey(status);
  if (key === "completed") return ENTITY_STATUS_STYLE.active;
  if (key === "failed") return ENTITY_STATUS_STYLE.suspended;
  if (key === "pending" || key === "processing" || key === "awaiting_3ds") return ENTITY_STATUS_STYLE.pending;
  if (key === "awaiting_checkout") return AWAITING_CHECKOUT_STATUS_STYLE;
  if (key === "refunded") return REFUNDED_STATUS_STYLE;
  return DEFAULT_STATUS_STYLE;
}

export function getOrderStatusLabel(status: unknown): string {
  const key = mapToOrderStatusKey(status);
  const labels: Record<string, string> = {
    completed: "Completed",
    failed: "Failed",
    pending: "Pending",
    processing: "Processing",
    awaiting_checkout: "Awaiting Checkout",
    awaiting_3ds: "Awaiting 3DS",
    refunded: "Refunded",
  };
  return labels[key] ?? formatEntityStatusLabel(status);
}

export function getTransactionModeStyle(testMode: unknown) {
  return Boolean(testMode) ? ENTITY_STATUS_STYLE.pending : ENTITY_STATUS_STYLE.active;
}

export function getTransactionModeLabel(testMode: unknown): string {
  return Boolean(testMode) ? "Sandbox" : "Live";
}
